// Photo library build: every photo input accepts what the iPhone library hands over.
//
// iOS gives HEIC for library photos (and, depending on the accept list, for camera shots too).
// The report renderer cannot draw HEIC, so a photo is turned into a JPEG on the device before it
// is queued or uploaded wherever the browser can decode it (Safari on iPhone always can). A
// browser that cannot decode it (desktop Chrome) sends the original, and the server converts it:
// server/src/lib/imageNormalise.ts. Very large photos are downscaled so a 48 megapixel library
// picture does not stall a slow upload. Orientation is applied while drawing (the bitmap is
// decoded with imageOrientation "from-image"), so the JPEG is upright and carries no orientation
// tag for anything to apply twice.

export const MAX_PHOTO_EDGE_PX = 3000;
export const PHOTO_JPEG_QUALITY = 0.85;
// A file already this small and within the edge limit is passed through untouched.
const PASS_THROUGH_BYTES = 6 * 1024 * 1024;

const HEIC_NAME = /\.(heic|heif)$/i;
const IMAGE_NAME = /\.(jpe?g|png|webp|gif|heic|heif)$/i;

export function isHeicFile(file: { name?: string; type?: string }): boolean {
  const t = (file.type ?? "").toLowerCase();
  return t === "image/heic" || t === "image/heif" || HEIC_NAME.test(file.name ?? "");
}

// iOS sometimes reports an empty type for a library HEIC, so the name decides when the type is blank.
export function isImageFile(file: { name?: string; type?: string }): boolean {
  const t = (file.type ?? "").toLowerCase();
  if (t.startsWith("image/")) return true;
  return t === "" && IMAGE_NAME.test(file.name ?? "");
}

export function scaledSize(width: number, height: number, maxEdge = MAX_PHOTO_EDGE_PX): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest <= 0) return { width, height };
  const k = maxEdge / longest;
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)) };
}

function jpegName(name: string): string {
  const base = (name || "photo").replace(/\.[A-Za-z0-9]+$/, "");
  return `${base || "photo"}.jpg`;
}

// Returns a JPEG File when the photo needed converting or shrinking and the browser could do it,
// otherwise the original file. Never throws: the original is always a valid thing to send.
export async function prepareImageForUpload(file: File): Promise<File> {
  const heic = isHeicFile(file);
  if (!heic && file.type !== "image/jpeg" && file.type !== "image/png" && file.type !== "image/webp") return file;
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return file;
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const { width, height } = scaledSize(bitmap.width, bitmap.height);
    const resized = width !== bitmap.width || height !== bitmap.height;
    if (!heic && !resized && file.size <= PASS_THROUGH_BYTES) return file;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", PHOTO_JPEG_QUALITY));
    if (!blob || blob.size === 0) return file;
    return new File([blob], jpegName(file.name), { type: "image/jpeg", lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    bitmap?.close?.();
  }
}

export async function prepareImagesForUpload(files: File[]): Promise<File[]> {
  const out: File[] = [];
  for (const f of files) out.push(await prepareImageForUpload(f));
  return out;
}

export const NOT_AN_IMAGE_MESSAGE = "Only image files can be uploaded.";
